import React, { useState, useRef } from 'react';
import {
  Box,
  Avatar,
  TextField,
  InputAdornment,
  CircularProgress,
  ClickAwayListener
} from '@mui/material';
import { ReactComponent as SearchIcon } from '../../../../assets/images/TicketImages/search.svg';
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import NoSearch from '../../../common/NoSearch';

const TicketGroupSelector = ({
  groups,
  setGroups,
  groupOptions,
  groupLoading,
  groupSearchTerm,
  setGroupSearchTerm,
  filteredGroups,
  getAvatarColor
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Styles
  const styles = {
    selector: {
      borderRadius: "8px",
      background: "#FFF",
      boxShadow: "0px 1px 2px rgba(6, 21, 34, 0.05)",
      border: "1px solid #D5DCE1",
      height: "40px",
      fontSize: "14px",
      padding: "0px 16px",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      transition: 'border-color 0.2s',

    },
    dropdown: {
      position: 'absolute',
      top: "calc(100% + 8px)",
      left: 0,
      right: 0,
      zIndex: 1300,
      // marginTop: "8px",
      border: "1px solid #E5E7EB",
      borderRadius: "8px",
      padding: "8px",
      maxHeight: "224px",
      backgroundColor: "#FFFFFF",
      boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.1)",
      display: 'flex',
      flexDirection: 'column'
    },
    searchField: {
      position: "sticky",
      top: 0,
      backgroundColor: "#fff",
      zIndex: 1000,
      marginBottom: '8px'
    },
    listContainer: {
      maxHeight: "220px",
      overflowY: "auto",
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      scrollbarWidth: 'thin',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '3px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#c1c1c1',
        borderRadius: '3px',
        '&:hover': {
          background: '#a8a8a8',
        },
      },
    },
    groupItem: {
      display: "flex",
      alignItems: "center",
      gap: '8px',
      fontSize: "14px",
      padding: '8px',
      fontFamily: "Inter",
      fontWeight: 400,
      textTransform: "capitalize",
      borderRadius: '8px',
      cursor: 'pointer',
      color: "#556575",
      transition: 'background-color 0.2s',
      '&:hover': {
        backgroundColor: "#F9FAFA !important",
        color: "#061522",
      }
    },
    selectedGroup: {
      backgroundColor: "#F9FAFA !important",
      color: "#061522 !important",   // ✅ make text black on selected
    },

    avatar: {
      width: 24,
      height: 24,
      fontSize: "0.75rem"
    },
    groupTitle: {
      fontSize: "14px",
      color: "#061522",
      fontFamily: "Inter",
      fontWeight: 400,
      textTransform: "capitalize",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "200px",
    },
    placeholder: {
      color: "#405261",
      fontSize: "14px",
      fontFamily: "Inter",
      fontWeight: 400,
    }
  };

  const selectedGroup = groupOptions.find(g => g.id === (groups.length > 0 ? groups[0] : "")) ||
    { title: typeof (groups.length > 0 ? groups[0] : "") === 'string' ? (groups.length > 0 ? groups[0] : "") : '' };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setGroupSearchTerm("");
    }
  };

  const handleSelectGroup = (group) => {
    setGroups([group.id]);
    setIsOpen(false);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  return (
    <div className="field-wrapper">
      <h3 className="field-label">Group</h3>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: 'relative', width: '100%' }}>
          {/* Main Selector Box */}
          <Box
            ref={containerRef}
            onClick={handleToggle}
            sx={styles.selector}
          >
            {/* Selected Value Display */}
            {!selectedGroup.title || groups.length === 0 ? (
              <span style={styles.placeholder}>
                Select group
              </span>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 }}>
                {selectedGroup.group_image ? (
                  <Avatar
                    src={selectedGroup.group_image}
                    alt={selectedGroup.title}
                    sx={styles.avatar}
                  />
                ) : (
                  <Avatar
                    sx={{
                      ...styles.avatar,
                      bgcolor: getAvatarColor(selectedGroup.title),
                      color: "#fff !important",
                    }}
                  >
                    {selectedGroup.title?.[0]?.toUpperCase()}
                  </Avatar>
                )}
                <span style={styles.groupTitle}>
                  {selectedGroup.title}
                </span>
              </Box>
            )}

            {/* Dropdown Arrow */}
            <Dropdown style={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0)",
              transition: "transform 0.2s",
              width: "16px",
              height: "16px",
            }} />
          </Box>

          {/* Dropdown List */}
          {isOpen && (
            <Box sx={styles.dropdown}>
              {/* Search Field */}
              <Box sx={styles.searchField}>
                <TextField
                  fullWidth
                  autoFocus
                  variant="outlined"
                  placeholder="Search group"
                  value={groupSearchTerm}
                  onChange={(e) => setGroupSearchTerm(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon className="search-icon-search" />
                      </InputAdornment>
                    ),
                    sx: {
                      "& fieldset": { border: "1px solid #D5DCE1" },
                      "& input": {
                        height: "40px",
                        padding: "0px 16px",
                        fontSize: "14px",
                        fontFamily: "Inter",
                        fontWeight: 400,
                        color: "#111827 !important",
                        cursor: "text !important",
                        backgroundColor: "transparent",
                        "&::placeholder": {
                          color: "#C5CED6",
                          opacity: 1,
                          fontSize: "14px",
                          fontFamily: "Inter",
                          fontWeight: 400,
                        },
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      "&.Mui-focused fieldset": { border: "1px solid #3B82F6" },
                    },
                  }}
                />
              </Box>
              {/* No Results State */}
              {!groupLoading && groupSearchTerm && filteredGroups.length === 0 && (
                <NoSearch
                  title="No group found"
                  description="Clear your search and try again."
                  titleFontSize={14}
                  descriptionFontSize={12}
                />
              )}

              {/* Scrollable List Container */}
              <Box sx={styles.listContainer}>
                {/* Loading State */}
                {groupLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", padding: "16px" }}>
                    <CircularProgress size={20} />
                  </Box>
                )}

                {/* Group Items */}
                {!groupLoading && (groupSearchTerm ? filteredGroups : groupOptions).map((group) => (
                  <Box
                    key={group.id}
                    onClick={() => handleSelectGroup(group)}
                    sx={{
                      ...styles.groupItem,
                      ...(groups.includes(group.id) && styles.selectedGroup)
                    }}
                  >
                    <Avatar
                      sx={{
                        height: 24,
                        width: 24,
                        fontSize: 14,
                        bgcolor: group.group_image ? "transparent" : getAvatarColor(group.title),
                      }}
                      src={group.group_image}
                    >
                      {!group.group_image && group.title.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ overflow: "hidden", flex: 1 }}>
                      < Box>
                        {group.title}
                      </  Box>
                    </Box>
                  </Box>
                ))}


              </Box>
            </Box>
          )}
        </Box>
      </ClickAwayListener>
    </div>
  );
};

export default TicketGroupSelector;